import { useState } from "react";
import {
	Box,
	Container,
	Card,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	TextField,
	MenuItem,
	Button,
	Chip,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_USER_EVENTS, LIST_USERS } from "../../gql/queries";
import { useShopDetails } from "../../hooks/use-shop";
import { Scrollbar } from "../../components/layouts/scrollbar";
import { NodataRow } from "../../components/generic/nodata.row";
import Loader from "../../components/generic/Loader";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface UserEvent {
	_id: string;
	userId: string;
	userName: string;
	eventType: string;
	shopId?: string;
	shopName?: string;
	locationId?: string;
	locationName?: string;
	referenceId?: string;
	details?: string;
	ipAddress?: string;
	createdAt: string;
}

interface PaginatedUserEvents {
	events: UserEvent[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

const eventTypeColors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
	USER_LOGIN: "success",
	USER_LOGOUT: "default",
	ORDER_CREATE: "primary",
	ORDER_UPDATE: "info",
	PRODUCT_CREATE: "secondary",
	PRODUCT_UPDATE: "info",
	SERVICE_CREATE: "secondary",
	SERVICE_UPDATE: "info",
	EXPENSE_CREATE: "warning",
	EXPENSE_UPDATE: "warning",
	CUSTOMER_CREATE: "primary",
	CUSTOMER_UPDATE: "info",
	SHOP_UPDATE: "info",
	LOCATION_UPDATE: "info",
	WHATSAPP_ORDER_SENT: "success",
	WHATSAPP_ORDER_FAILED: "error",
};

const formatEventType = (eventType: string): string => {
	return eventType
		.split("_")
		.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
		.join(" ");
};

const UserEventsPage = () => {
	const { shopId, locationId } = useShopDetails();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [startDate, setStartDate] = useState<Date | null>(
		new Date(new Date().setDate(new Date().getDate() - 7))
	);
	const [endDate, setEndDate] = useState<Date | null>(new Date());

	// Fetch users for the filter dropdown
	const { data: usersData } = useQuery(LIST_USERS, {
		variables: { shopId, locationId },
		skip: !shopId || !locationId,
	});

	// Fetch user events
	const { data, loading, refetch } = useQuery<{ userEvents: PaginatedUserEvents }>(
		GET_USER_EVENTS,
		{
			variables: {
				input: {
					userId: selectedUserId || undefined,
					startDate: startDate?.toISOString(),
					endDate: endDate?.toISOString(),
					page: page + 1,
					limit: rowsPerPage,
				},
			},
			skip: !shopId || !locationId,
			fetchPolicy: "network-only",
		}
	);

	const events = data?.userEvents?.events || [];
	const total = data?.userEvents?.total || 0;

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleApplyFilters = () => {
		setPage(0);
		refetch();
	};

	const handleResetFilters = () => {
		setSelectedUserId("");
		setStartDate(new Date(new Date().setDate(new Date().getDate() - 7)));
		setEndDate(new Date());
		setPage(0);
		refetch();
	};

	return (
		<>
			<Loader isLoading={loading} />
			<Box component="main" sx={{ flexGrow: 1, py: 8 }}>
				<Container maxWidth="xl">
					<Stack spacing={3}>
						<Typography variant="h4">User Activity Log</Typography>

						{/* Filters */}
						<Card sx={{ p: 3 }}>
							<Stack spacing={3}>
								<Typography variant="h6">Filters</Typography>
								<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
									<TextField
										select
										label="User"
										value={selectedUserId}
										onChange={(e) => setSelectedUserId(e.target.value)}
										sx={{ minWidth: 200 }}
										size="small"
									>
										<MenuItem value="">All Users</MenuItem>
										{usersData?.users?.map((user: any) => (
											<MenuItem key={user._id} value={user._id}>
												{user.firstName} {user.lastName}
											</MenuItem>
										))}
									</TextField>

									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											label="Start Date"
											value={startDate}
											onChange={(newValue) => setStartDate(newValue)}
											slotProps={{ textField: { size: "small" } }}
										/>
										<DatePicker
											label="End Date"
											value={endDate}
											onChange={(newValue) => setEndDate(newValue)}
											slotProps={{ textField: { size: "small" } }}
										/>
									</LocalizationProvider>

									<Button variant="contained" onClick={handleApplyFilters}>
										Apply
									</Button>
									<Button variant="outlined" onClick={handleResetFilters}>
										Reset
									</Button>
								</Stack>
							</Stack>
						</Card>

						{/* Events Table */}
						<Card>
							<Scrollbar>
								<Box sx={{ minWidth: 1200 }}>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>Date & Time</TableCell>
												<TableCell>User</TableCell>
												<TableCell>Event Type</TableCell>
												<TableCell>Details</TableCell>
												<TableCell>Reference ID</TableCell>
												<TableCell>IP Address</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{events.length === 0 && <NodataRow colSpan={6} />}
											{events.map((event) => (
												<TableRow hover key={event._id}>
													<TableCell>
														{new Date(event.createdAt).toLocaleString()}
													</TableCell>
													<TableCell>
														<Typography variant="body2" fontWeight="medium">
															{event.userName}
														</Typography>
													</TableCell>
													<TableCell>
														<Chip
															label={formatEventType(event.eventType)}
															color={eventTypeColors[event.eventType] || "default"}
															size="small"
														/>
													</TableCell>
													<TableCell>
														<Typography variant="body2" sx={{ maxWidth: 300 }}>
															{event.details || "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography
															variant="body2"
															sx={{
																fontFamily: "monospace",
																fontSize: "0.75rem",
															}}
														>
															{event.referenceId
																? event.referenceId.substring(0, 8) + "..."
																: "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
															{event.ipAddress || "-"}
														</Typography>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</Box>
							</Scrollbar>
							<TablePagination
								component="div"
								count={total}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								page={page}
								rowsPerPage={rowsPerPage}
								rowsPerPageOptions={[25, 50, 100]}
							/>
						</Card>
					</Stack>
				</Container>
			</Box>
		</>
	);
};

export default UserEventsPage;
